import { useState } from "react";
import type { Comment, SubmissionView, Thread } from "@/app/reviewQueries";
import { initials } from "@/app/nav";
import { Button } from "@/ui/Button";
import { fmtWhen, isStudent } from "./model";

/* Тред у ячейки (S7, M2): корень ментора + ответы. Аватары по роли — ментор
   card2/ink, студент accsoft/acc (макет S7). */

export function RoleAvatar({ name, student, size = 26 }: { name: string; student: boolean; size?: number }) {
  return (
    <span
      aria-hidden
      className="grid flex-none place-items-center rounded-full border font-mono text-[10.5px] font-semibold"
      style={{ width: size, height: size, background: student ? "var(--accsoft)" : "var(--card2)", color: student ? "var(--acc)" : "var(--ink)", borderColor: "var(--bd2)" }}
    >
      {initials(name)}
    </span>
  );
}

export function Message({ c, view }: { c: Comment; view: Pick<SubmissionView, "student"> }) {
  const student = isStudent(c, view);
  return (
    <div className="grid grid-cols-[26px_minmax(0,1fr)] gap-2.5" data-testid="message" data-role={student ? "student" : "mentor"}>
      <RoleAvatar name={c.author.name} student={student} />
      <div className="min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-[12.5px] font-semibold text-ink">{c.author.name}</span>
          <span className="font-mono text-[10.5px] text-ink2">{student ? "студент" : "ментор"}</span>
          <span className="font-mono text-[10.5px] text-ink2">{fmtWhen(c.created_at)}</span>
          {c.reason && (
            <span className="rounded-[3px] border border-bd2 px-1.5 py-px font-mono text-[10.5px] text-ink2" data-testid="reason-tag">
              {c.reason.label}
            </span>
          )}
        </div>
        <p className="mb-0 mt-1 whitespace-pre-line text-[13px] leading-[1.55] text-ink2 text-pretty">{c.body}</p>
        {!c.parent_id && c.todo && (
          <p className="mb-0 mt-1 font-mono text-[11px] leading-[1.5] text-ink2">
            <span style={{ color: "var(--err)" }}>→</span> {c.todo}
          </p>
        )}
      </div>
    </div>
  );
}

/** Ответ в треде: пустой текст не отправляется — причина рядом. */
export function ReplyComposer({ me, onSend, placeholder = "ответить в треде" }: { me: { name: string; student: boolean }; onSend: (body: string) => Promise<void>; placeholder?: string }) {
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    const body = text.trim();
    if (!body) {
      setError("Пустой ответ не отправится — напиши текст.");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await onSend(body);
      setText("");
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : "Не удалось отправить ответ. Текст остался в поле — попробуй ещё раз.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="grid grid-cols-[26px_minmax(0,1fr)] gap-2.5" data-testid="reply">
      <RoleAvatar name={me.name} student={me.student} />
      <div className="flex flex-col gap-1.5">
        <div className="flex gap-2">
          <input
            value={text}
            onChange={(e) => (setText(e.target.value), setError(null))}
            onKeyDown={(e) => e.key === "Enter" && void send()}
            placeholder={placeholder}
            aria-label={placeholder}
            className="min-w-0 flex-1 rounded-sm border border-bd2 bg-bg2 px-[11px] py-[7px] text-[13px] text-ink outline-none focus:border-acc"
          />
          <Button variant="primary" size="sm" className="py-[7px]" onClick={() => void send()} disabled={busy} reason={busy ? "отправляем…" : undefined}>
            отправить
          </Button>
        </div>
        {error && (
          <p role="alert" className="m-0 text-[12px] leading-[1.5]" style={{ color: "var(--err)" }}>
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

export function ThreadView({ thread, view, reply }: { thread: Thread; view: Pick<SubmissionView, "student">; reply?: { me: { name: string; student: boolean }; onSend: (body: string) => Promise<void> } }) {
  return (
    <div className="flex flex-col gap-3 border-t border-bd bg-card px-3.5 py-3" data-testid="thread" data-root={thread.root.id}>
      <Message c={thread.root} view={view} />
      {thread.replies.map((r) => (
        <Message key={r.id} c={r} view={view} />
      ))}
      {reply && <ReplyComposer me={reply.me} onSend={reply.onSend} />}
    </div>
  );
}
